import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";

function Blocks({ blocks }) {
  return blocks.map((b, i) => {
    if (b.t === "h2")
      return (
        <h2 key={i} className="mt-11 mb-3 text-xl md:text-2xl font-bold tracking-tight text-[var(--mh-charcoal)]" data-testid="legal-section-heading">
          {b.text}
        </h2>
      );
    if (b.t === "ul")
      return (
        <ul key={i} className="my-3 pl-6 space-y-2 list-disc marker:text-[var(--mh-gold)]">
          {b.items.map((it, j) => (
            <li key={j} className="text-[15px] leading-relaxed text-[#413D3A]">{it}</li>
          ))}
        </ul>
      );
    return (
      <p key={i} className="my-3 text-[15px] leading-relaxed text-[#413D3A]">{b.text}</p>
    );
  });
}

export function LegalLayout({ docTitle, title, effectiveDate, intro = [], blocks = [], testid }) {
  useEffect(() => {
    const prev = document.title;
    document.title = docTitle;
    return () => { document.title = prev; };
  }, [docTitle]);

  return (
    <div className="min-h-screen bg-[var(--mh-soft-white)] text-[var(--mh-charcoal)] antialiased" data-testid={testid}>
      {/* Nav */}
      <nav className="px-6 md:px-12 lg:px-20 py-6 border-b border-[var(--mh-border)]">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="legal-logo">
            <Logo theme="black" height={30} />
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login" className="text-sm text-[var(--mh-muted-text)] hover:text-[var(--mh-charcoal)] hidden sm:inline px-3 py-2 transition-colors">Sign in</Link>
            <Link to="/signup" data-testid="legal-nav-cta">
              <Button className="bg-[var(--mh-charcoal)] hover:bg-black text-white rounded-full px-5 h-10 transition-colors">Get Started</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Document */}
      <article className="px-6 md:px-12 lg:px-20 py-12 md:py-16">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight" data-testid="legal-title">{title}</h1>
          <div className="mt-4 inline-flex items-center rounded-full bg-white border border-[var(--mh-border)] px-4 py-1.5 mh-shadow-soft" data-testid="legal-effective-date">
            <span className="text-sm font-medium text-[var(--mh-charcoal)]">Effective Date: {effectiveDate}</span>
          </div>

          <div className="mt-8">
            {intro.map((p, i) => (
              <p key={i} className="my-3 text-[15px] leading-relaxed text-[#413D3A]">{p}</p>
            ))}
            <Blocks blocks={blocks} />
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="px-6 md:px-12 lg:px-20 py-12 bg-[var(--mh-soft-white)] border-t border-[var(--mh-border)]">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <Logo theme="black" height={24} />
          <div className="flex items-center gap-5 text-sm text-[var(--mh-muted-text)]">
            <Link to="/terms" className="hover:text-[var(--mh-charcoal)] transition-colors" data-testid="legal-footer-terms">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-[var(--mh-charcoal)] transition-colors" data-testid="legal-footer-privacy">Privacy Policy</Link>
            <Link to="/pricing" className="hover:text-[var(--mh-charcoal)] transition-colors">Pricing</Link>
          </div>
        </div>
        <p className="max-w-3xl mx-auto mt-8 text-xs text-[var(--mh-muted-text)]">© {new Date().getFullYear()} MinistryHub · Operated by Up &amp; At ’Em Design Co. LLC</p>
      </footer>
    </div>
  );
}
